import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import IconCancel from '@/icons/Cancel.svg';
import { useConfirmExchange } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';

import { ArticleActionProps } from '../ArticleActions';

import { CancelExchangeDialog } from './CancelExchangeDialog';

export const EchangeArticleAction = ({ onActionDone }: ArticleActionProps) => {
  const { t } = useLingui();

  const { article, state, hasError, view } = useArticleContext();

  const [apiError, setApiError] = useState<string | null>(null);
  const [isCancelExchangeDialogOpen, setIsCancelExchangeDialogOpen] = useState(false);

  const { mutateAsync: confirmExchange, isPending: isPendingConfirmExchange } = useConfirmExchange({
    articleId: article.id,
  });

  const handleConfirmExchange = async () => {
    const { hasError } = state.errors.check({
      services: {
        exchange: {
          missingReplacementUniqueArticle:
            view.services.exchange.replacementUniqueArticle.shown &&
            view.services.exchange.replacementUniqueArticle.editable &&
            view.services.exchange.replacementUniqueArticle.required,
        },
      },
    });

    setApiError(null);

    if (!hasError) {
      try {
        await confirmExchange();
        onActionDone();
      } catch (err: any) {
        console.error(err);
        setApiError(
          (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
        );
      }
    }
  };

  return (
    <>
      <Footer>
        {(hasError || apiError) && (
          <FooterError>
            {hasError ? (
              <Trans id="article.actions.exchange_article.errors">
                Please fill the missing information in order to confirm the exchange
              </Trans>
            ) : (
              apiError
            )}
          </FooterError>
        )}
        <FooterButtons>
          <Button
            variant="secondary-danger"
            size="medium"
            onPress={() => setIsCancelExchangeDialogOpen(true)}
            disabled={isPendingConfirmExchange}
            className="text-no-wrap"
          >
            <IconCancel />
            <Trans id="article.actions.exchange_article.cancel">Cancel exchange</Trans>
          </Button>
          <Button
            variant="primary"
            size="medium"
            onPress={handleConfirmExchange}
            isLoading={isPendingConfirmExchange}
            className="text-no-wrap"
          >
            <Trans id="article.actions.exchange_article.confirm">Confirm exchange</Trans>
          </Button>
        </FooterButtons>
      </Footer>
      <CancelExchangeDialog
        isOpen={isCancelExchangeDialogOpen}
        setIsOpen={setIsCancelExchangeDialogOpen}
        onActionDone={onActionDone}
      />
    </>
  );
};
